export enum EventName {
  SKU_VIEWED = 'SKU Viewed',
  SHOPPING_FINISHED = 'Shopping Finished',
  PARTNER_CTA_CLICK = 'partner_cta_clicked',
  ELEMENT_VIEWED = 'Element Viewed',
  ELEMENT_INTERACTED = 'Element Interacted',
  FORM_ACTION = 'Form Action',
  LINK_VIEWED = 'Link Viewed',
  LINK_CLICKED = 'Link Clicked',
}

export interface BaseEvent {
  deployable_name: string;
  deployable_version: string;
  traditional_device_id: string;
}

export interface BaseShoppingEvent {
  sku_id?: string;
  sku_source?: 'marketplace' | 'plinko' | 'treehouse';
  sku_name?: string;
  sku_product_type?: string;
  sku_level?: 'institution' | 'offer' | 'product';
  institution_id?: string;
  institution_name?: string;
  product_id?: string;
  product_name?: string;
  offer_id?: string;
  offer_name?: string;
  monetizable?:
    | 'yes_assumed'
    | 'no'
    | 'no_because_of_limit'
    | 'no_because_of_lead'
    | 'no_because_of_lead_form'
    | 'no_because_of_details';
  entry_point_pageview_id?: string;
  entry_point_page_url?: string;
  entry_point_page_name?: string;
  impression_pageview_id?: string;
  impression_page_url?: string;
  impression_page_name?: string;
  impression_section?: string;
  impression_position?: number;
  sku_impression_id: string;
  additional_attributes?: Record<string, unknown>;
}

export type Event<T> = BaseEvent & T;

type EventStandardized<T> = Omit<BaseEvent, 'traditional_device_id'> & T;

export type PagePayload = Event<{
  pageview_id: string;
  page_type?: string;
  content_id?: string | number;
  content_source?: 'wp';
  path?: string;
  url?: string;
}>;

export type SkuViewedPayload = Event<BaseShoppingEvent>;

export type ShoppingFinishedPayload = Event<
  BaseShoppingEvent & {
    shopping_finish_id: string;
    sub_id_alias?: string;
    finish_type: 'external_application' | 'internal_content' | 'lead_inputflow';
    destination_url: string;
    cta_type?: string;
    revenue_amount?: string | number;
    revenue_type?: 'ingested' | 'estimated';
    interstitial_client_bot_suspected?: boolean;
    additional_attributes?: Record<string, unknown>;
  }
>;

export interface BaseElementEvent {
  event_id: string;
  entity_name: string;
  section_name: string;
  pageview_id: string;
  page_name: string;
  page_url: string;
  item_position?: number;
  item_copy?: string;
  additional_attributes?: Record<string, unknown>;
}

export type ElementViewedPayload = EventStandardized<BaseElementEvent>;

export type ElementInteractedPayload = EventStandardized<
  BaseElementEvent & {
    parent_event_id: string;
    interaction_type: string;
  }
>;

export type FormActionType = 'viewed' | 'field-interacted' | 'submitted' | 'error';

export type FormActionPayload = EventStandardized<{
  event_id: string;
  parent_event_id?: string;
  form_action: FormActionType;
  form_name: string;
  section_name?: string;
  pageview_id: string;
  page_name: string;
  page_url: string;
  field?: string;
  field_type?: string;
  field_value?: string;
  additional_attributes?: Record<string, unknown>;
}>;

export interface BaseLinkEvent {
  event_id: string;
  entity_name: string;
  section_name?: string;
  pageview_id: string;
  page_name: string;
  page_url: string;
  destination_url: string;
  destination_content_id?: string;
  link_position?: number;
  link_copy?: string;
  additional_attributes?: Record<string, unknown>;
}

export type LinkViewedPayload = EventStandardized<BaseLinkEvent>;

export type LinkClickedPayload = EventStandardized<
  BaseLinkEvent & {
    parent_event_id: string;
  }
>;

export type EventPayload<T extends EventName> = T extends EventName.SKU_VIEWED
  ? SkuViewedPayload
  : T extends EventName.SHOPPING_FINISHED | EventName.PARTNER_CTA_CLICK
    ? ShoppingFinishedPayload
    : T extends EventName.ELEMENT_VIEWED
      ? ElementViewedPayload
      : T extends EventName.ELEMENT_INTERACTED
        ? ElementInteractedPayload
        : T extends EventName.FORM_ACTION
          ? FormActionPayload
          : T extends EventName.LINK_VIEWED
            ? LinkViewedPayload
            : T extends EventName.LINK_CLICKED
              ? LinkClickedPayload
              : never;
